import { useCallback, useMemo } from 'react'

import { useUserContext } from '@/context'

import { PageViewProperties, UserEngagementProperties } from './useAnalytics.types'
import { heapIdentify, heapTrack } from './useAnalytics.util'

type PageViewOptions = { [key in keyof PageViewProperties]: any }
type UserEngagementOptions = { [key in keyof UserEngagementProperties]: any }

export function useAnalytics() {
  const { isLoggedIn, user } = useUserContext()

  const analyticPageName = useMemo(() => {}, [])

  const identifyUser = useCallback(
    (email?: string) => {
      // * heap requires an email address to identify a user: https://developers.heap.io/docs/using-identify
      if (email) heapIdentify({ email, role: user?.role ?? 'null' })
    },
    [user?.role],
  )

  const trackEventForHeap = useCallback((eventName: string, eventProperties: Record<string, any>) => {
    heapTrack(eventName, toStringValues(eventProperties))
  }, [])

  const trackPage = useCallback(
    (properties?: PageViewOptions) => {
      trackEventForHeap('pageview', { url: window.location.href, page_name: analyticPageName, ...properties })
    },
    [trackEventForHeap, analyticPageName],
  )

  const trackUserEngagement = useCallback(
    (properties: UserEngagementOptions) => {
      trackEventForHeap('user_engagement', {
        url: window.location.href,
        page_name: analyticPageName,
        email: user?.email,
        logged_in: isLoggedIn,
        user_role: user?.role,
        ...properties,
      })
    },
    [trackEventForHeap, analyticPageName, user?.email, user?.role, isLoggedIn],
  )

  return {
    analyticPageName,
    identifyUser,
    trackEventForHeap,
    trackPage,
    trackUserEngagement,
  }
}

export function toStringValues(eventProperties: Record<string, any>): { [key: string]: string } {
  return Object.entries(eventProperties).reduce((acc, [key, value]) => {
    return { ...acc, [key]: Array.isArray(value) ? toArrayValue(value) : value?.toString() || '' }
  }, {})
}

function toArrayValue(arr?: string[]): string | undefined {
  return arr?.join(',')
}

export function removeSpecialCharacters(str?: string | null) {
  return str?.replace(/[^0-9a-zA-Z\s]+/, '') || ''
}
